import { EventEmitter, InjectionToken, Type } from '@angular/core';
import { ProviderName } from '../../../account/models/login-response.model';
import { GiftCodeModel } from '../../../purchase/models/giftcode/gift-code.model';

/* ================================
 * Types
 * ================================ */

/**
 * The context passed from the express checkout container down to each express checkout provider
 * component (e.g. Paze). Contains everything a provider needs to launch its wallet flow.
 */
export interface ExpressCheckoutContext {
  /**
   * Which express checkout endpoints the providers should drive:
   *  - 'marketplace' pays an order that already exists, via the Checkout V4 endpoints
   *    (api/checkout/express/*). It identifies the basket by `orderId` and sends no body, because
   *    the server already holds the basket against that order - so `basketItems` is unused here;
   *  - 'registration' pays the current registration's basket (api/register/express/*), which does
   *    require `basketItems`;
   *  - 'card-token' saves a payment method (api/cardtokens/express/*).
   * Defaults to 'card-token' when omitted, preserving the original behaviour.
   */
  mode?: ExpressCheckoutMode;
  /** The order id being paid for. */
  orderId: string;
  /** The amount to charge, as the real value coming from the api/order (not display formatted). */
  amount: number;
  /** ISO currency code, e.g. 'USD'. */
  currencyCode: string;
  /** Customer email used to look up an eligible wallet. Either this or mobileNumber should be set. */
  emailAddress?: string;
  /** Customer mobile number used to look up an eligible wallet. Either this or emailAddress should be set. */
  mobileNumber?: string;
  /**
   * Basket items to purchase. Required when `mode` is 'registration'; ignored for card-token and
   * for 'marketplace', which identifies the basket by `orderId` instead.
   */
  basketItems?: unknown[];
  /** Member id, used by the express endpoints that take one as a query parameter. */
  memberId?: string;
  /** Whether the marketplace purchase should use a stored card token. */
  useCardToken?: boolean;
  /**
   * Checkout V4 only: when true the payment mode becomes Subscription and a card token item is
   * included, so the card is kept for future charges. Must be the same value on init and complete,
   * which is why it lives on the context rather than being passed to each call. Defaults to false.
   */
  saveCardToken?: boolean;
  /** Relative url to navigate to on a successful express checkout. */
  successUrl?: string;
  /** Relative url to navigate to on a failed express checkout. */
  failureUrl?: string;
  giftCodes?: GiftCodeModel[];
}

export interface MarketplaceExpressCheckoutContext {
  mode?: Extract<ExpressCheckoutMode, 'marketplace' | 'registration'>;
  orderId?: string;
  emailAddress?: string;
  mobileNumber?: string;
  basketItems?: unknown[];
  useCardToken: boolean;
  saveCardToken?: boolean;
  successUrl: string;
  failureUrl: string;
  giftCodes?: GiftCodeModel[];
}

/** Selects which express checkout endpoint family a provider drives for a given context. */
export type ExpressCheckoutMode = 'marketplace' | 'registration' | 'card-token';

/**
 * The outcome of an express checkout attempt, re-emitted by the container to the host page.
 */
export interface ExpressCheckoutResult {
  /** The provider that produced this result. */
  providerId: ProviderName;
  /** Whether the express checkout completed and a payment payload was captured. */
  success: boolean;
  /** Optional provider payload/reference (e.g. the order id) for the host to act on. */
  data?: unknown;
  /** Optional error information when success is false. */
  error?: { reason?: string; message?: string };
}

/**
 * Callbacks a provider service uses to report the outcome of its checkout flow back to the
 * component, which forwards them to its `result` / `incomplete` outputs.
 */
export interface ExpressCheckoutHandlers {
  /** The express checkout completed (payment payload captured / submitted). */
  onResult: (result: ExpressCheckoutResult) => void;
  /** The customer was ineligible / cancelled — the host should restore the native flow. */
  onIncomplete: (result: ExpressCheckoutResult) => void;
}

/**
 * Contract every express checkout provider component must implement so the container can drive it
 * uniformly. Providers extend BaseExpressCheckoutComponent which fulfils this contract.
 */
export interface ExpressCheckoutProvider {
  /** Injected by the container before the flow starts. */
  context: ExpressCheckoutContext;
  /** Emitted when the express checkout finishes (success or failure). */
  result: EventEmitter<ExpressCheckoutResult>;
  /** Emitted when the customer is ineligible / cancels, so the host can restore the native flow. */
  incomplete: EventEmitter<ExpressCheckoutResult>;
}

/**
 * Registration entry describing an express checkout provider and the component that renders it.
 */
export interface ExpressCheckoutProviderOptions {
  component: Type<ExpressCheckoutProvider>;
  options?: Record<string, any>;
}

/* ================================
 * Injection Tokens
 * ================================ */

/**
 * Registry of express checkout provider components, keyed by ProviderName. Populated by the
 * consuming app (mirrors PAYMENT_TEMPLATES) and consumed by the ExpressCheckoutComponent container
 * to dynamically render every available express provider.
 */
export const EXPRESS_CHECKOUT_PROVIDERS = new InjectionToken<Partial<Record<ProviderName, ExpressCheckoutProviderOptions>>>(
  'expressCheckoutProviders',
  {
    providedIn: 'root',
    factory: () => ({}),
  }
);
